import { Button, Card } from '@/components/UI';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import {
    formatAeroNumber,
    formatAeroPercent,
    formatAeroWinRatePercent
} from '@/features/tradeLogicAuthoring/components/aeroCandidateResultDetails';
import { isAeroCandidatePromotable } from '@/features/tradeLogicAuthoring/components/aeroCandidatePromotionEligibility';
import ExitConfigChips from '@/features/tradeLogicAuthoring/components/ExitConfigChips';
import type { AeroOptionBuyingStrategyLeader } from '@/features/tradeLogicAuthoring/api';
import { type AeroObsPalette, buildAeroObsKey, getAeroObsTone } from '../aeroObsPalette';
import { formatAeroCompactNumber } from '../aeroPageUtils';

export type AeroResultSortBy = 'selection' | 'score' | 'avgProfit' | 'winsorizedScore' | 'winsorizedAvgProfit';

interface AeroObsLeaderboardProps {
    palette: AeroObsPalette;
    leaders: AeroOptionBuyingStrategyLeader[];
    isLoading: boolean;
    sortBy: AeroResultSortBy;
    activeObsKey: string | null;
    onSelectObs: (obsKey: string | null) => void;
    onUseResult: (variantHash: string) => void;
}

const LeaderboardMetric = ({ label, value }: { label: string; value: string }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
        <div className="text-sm font-medium text-zinc-100">{value}</div>
    </div>
);

const AeroObsLeaderboard = ({ palette, leaders, isLoading, sortBy, activeObsKey, onSelectObs, onUseResult }: AeroObsLeaderboardProps) => {
    const sortLabel =
        sortBy === 'selection'
            ? 'the experiment selection policy'
            : sortBy === 'avgProfit'
              ? 'raw average profit'
              : sortBy === 'winsorizedAvgProfit'
                ? 'winsorized average profit'
                : sortBy === 'score'
                  ? 'raw composite score'
                  : 'winsorized composite score';

    return (
        <Card
            className="border-cyan-900/40"
            subtitle={`Best exit configuration for each option-buying strategy, ranked by ${sortLabel} across every candidate on that axis.`}
            title="Which option strategy wins?"
        >
            {isLoading ? (
                <DataLoadingSpinner text="Ranking option strategies..." />
            ) : leaders.length === 0 ? (
                <div className="rounded-md border border-zinc-800 bg-black/20 p-4 text-sm text-zinc-400">
                    No option-buying axes were scored for this job.
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {leaders.map((leader) => {
                        const obsKey = buildAeroObsKey(leader.optionBuyingStrategyId);
                        const tone = getAeroObsTone(palette, leader.optionBuyingStrategyId);
                        const isActive = activeObsKey === obsKey;
                        const candidate = leader.result;
                        const promotable = isAeroCandidatePromotable(candidate);

                        return (
                            <div
                                className={[
                                    'flex flex-col gap-3 rounded-lg border bg-black/30 p-3 transition-shadow',
                                    tone.borderClassName,
                                    isActive ? `ring-2 ${tone.ringClassName}` : ''
                                ]
                                    .filter(Boolean)
                                    .join(' ')}
                                key={obsKey}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`h-2 w-2 shrink-0 rounded-full ${tone.dotClassName}`} />
                                            <div className="truncate text-sm font-semibold text-zinc-100" title={tone.label}>
                                                {leader.optionBuyingStrategyName ?? tone.shortLabel}
                                            </div>
                                        </div>
                                        <div className="mt-0.5 text-[11px] text-zinc-500">
                                            Best of {formatAeroCompactNumber(leader.axisCandidateCount)} exit config
                                            {leader.axisCandidateCount === 1 ? '' : 's'}
                                        </div>
                                    </div>
                                    <Button
                                        size="sm"
                                        variant={isActive ? 'primary' : 'outline-dark'}
                                        onClick={() => onSelectObs(isActive ? null : obsKey)}
                                    >
                                        {isActive ? 'Showing' : 'Explore'}
                                    </Button>
                                </div>

                                <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
                                    <LeaderboardMetric label="Selected score" value={formatAeroNumber(candidate.selectedLiveScore)} />
                                    <LeaderboardMetric label="Raw avg" value={formatAeroPercent(candidate.avgProfit)} />
                                    <LeaderboardMetric label="Winsorized avg" value={formatAeroPercent(candidate.winsorizedAvgProfit)} />
                                    <LeaderboardMetric label="Win rate" value={formatAeroWinRatePercent(candidate.winRate)} />
                                    <LeaderboardMetric label="Trades" value={formatAeroCompactNumber(candidate.totalTrades)} />
                                </div>

                                <ExitConfigChips candidate={candidate} />

                                <div className="flex items-center justify-between gap-2 pt-1">
                                    <div className="text-[11px] text-zinc-500">PF {formatAeroNumber(candidate.profitFactor)}</div>
                                    <Button
                                        disabled={!promotable}
                                        size="sm"
                                        variant="outline-dark"
                                        onClick={() => onUseResult(candidate.variantHash)}
                                    >
                                        Use this result
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </Card>
    );
};

export default AeroObsLeaderboard;
